import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, UserPlus, User, Eye, EyeOff } from 'lucide-react';
import { spacing, fontWeight } from '../../styles/theme';
import { webvonkClient } from '../../lib/supabase';
import { useSession } from '../../contexts/SessionContext';
import BackButton from '../components/BackButton';

const WEBVONK_URL = 'https://swxfaqoftmwphbparotf.supabase.co';
const WEBVONK_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN3eGZhcW9mdG13cGhicGFyb3RmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk1MjY1OTQsImV4cCI6MjA5NTEwMjU5NH0.IZZAiD-ndE_CvkuY3E44N8Sm7u1EXnsYaYnCda6R-v4';

const ROLES = {
  '1': 'Ontwikkelaar',
  '2': 'Eigenaar',
  '3': 'Medewerker',
};

const ROLE_COLORS = {
  '1': '#f97316',
  '2': '#3b82f6',
  '3': 'rgba(255,255,255,0.4)',
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const slideDown = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

const Toast = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  padding: 10px 18px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  animation: ${slideDown} 0.25s ease;
  background: ${props => props.$type === 'error' ? '#ef4444' : '#16a34a'};
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
`;

const Container = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
  transition: filter 0.25s ease, opacity 0.25s ease;
  filter: ${props => props.$blurred ? 'blur(6px)' : 'none'};
  opacity: ${props => props.$blurred ? 0.4 : 1};
  pointer-events: ${props => props.$blurred ? 'none' : 'auto'};
`;

const Content = styled.div`
  padding: 72px ${spacing.lg} ${spacing.xl};
  max-width: 540px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 72px ${spacing.md} ${spacing.xl};
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.02em;
  margin: 0 0 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 8px;
  padding-left: 4px;
  opacity: 0.6;
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.06);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 13px 14px;
  &:not(:last-child) { border-bottom: 1px solid rgba(255,255,255,0.05); }
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.4);
  flex-shrink: 0;
  margin-right: 12px;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const Username = styled.div`
  font-size: 15px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.01em;
`;

const RoleBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color};
`;

const StatusDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${props => props.$active ? '#22c55e' : '#ef4444'};
  margin-right: 12px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  cursor: pointer;
  color: rgba(255,255,255,0.35);
  padding: 5px 7px;
  display: flex;
  align-items: center;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(239,68,68,0.1);
    border-color: rgba(239,68,68,0.2);
    color: #ef4444;
  }
`;

const AddRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 14px;
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
  color: #f97316;
  font-size: 15px;
  font-weight: ${fontWeight.medium};
  transition: background 0.15s ease;

  &:hover { background: rgba(255,255,255,0.02); }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: grid;
  place-items: center;
  z-index: 99999;
  padding: ${spacing.lg};
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const ModalContent = styled.div`
  background: #111;
  border-radius: 18px;
  width: 90%;
  max-width: 400px;
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 32px 80px rgba(0,0,0,0.7);

  @keyframes slideUp {
    from { transform: translateY(16px); opacity: 0; }
    to   { transform: translateY(0); opacity: 1; }
  }
  animation: slideUp 0.25s cubic-bezier(0.25,0.46,0.45,0.94);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  letter-spacing: -0.01em;
`;

const CloseBtn = styled.button`
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: rgba(255,255,255,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255,255,255,0.1); color: #fff; }
`;

const ModalBody = styled.div`
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.35);
  margin-bottom: 6px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 13px;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  background: rgba(255,255,255,0.05);
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus { outline: none; border-color: #f97316; box-shadow: 0 0 0 3px rgba(249,115,22,0.1); }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const PasswordWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(255,255,255,0.3);
  display: flex;
  padding: 0;
  &:hover { color: rgba(255,255,255,0.6); }
`;

const SegmentControl = styled.div`
  display: flex;
  background: rgba(255,255,255,0.05);
  border-radius: 10px;
  padding: 3px;
  gap: 3px;
`;

const Segment = styled.button`
  flex: 1;
  padding: 7px 4px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${props => props.$active ? '#f97316' : 'transparent'};
  color: ${props => props.$active ? '#fff' : 'rgba(255,255,255,0.45)'};
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
`;

const ToggleTrack = styled.button`
  width: 44px;
  height: 26px;
  border-radius: 13px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.25s ease;
  background: ${props => props.$on ? '#22c55e' : 'rgba(255,255,255,0.12)'};

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    top: 3px;
    left: ${props => props.$on ? '21px' : '3px'};
    transition: left 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: 0 2px 6px rgba(0,0,0,0.25);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 18px 18px;
`;

const PrimaryBtn = styled.button`
  flex: 1;
  padding: 12px;
  background: ${props => props.$danger ? '#ef4444' : '#f97316'};
  color: #fff;
  border: none;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(0.9); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SecondaryBtn = styled.button`
  flex: 1;
  padding: 12px;
  background: transparent;
  color: rgba(255,255,255,0.4);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover { color: rgba(255,255,255,0.6); }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function UsersScreen() {
  const { user: currentUser } = useSession();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRoleId, setNewRoleId] = useState('3');
  const [newActive, setNewActive] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const myRoleId = String(currentUser?.role?.Role_id || currentUser?.role_id || '3');
  const companySlug = currentUser?.company_slug;
  const authToken = (() => {
    try { return JSON.parse(localStorage.getItem('kmfc_user') || '{}')?.access_token; } catch { return null; }
  })();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const canDelete = (u) => {
    if (u.User_id === currentUser?.id) return false;
    const targetRole = String(u.Role_id);
    if (myRoleId === '1') return targetRole === '2' || targetRole === '3';
    if (myRoleId === '2') return targetRole === '3';
    return false;
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${WEBVONK_URL}/rest/v1/rpc/admin_get_users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'apikey': WEBVONK_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ p_company_slug: companySlug }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data || []);
    } catch (e) {
      console.error(e);
      showToast('Kon gebruikers niet laden', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companySlug) loadUsers();
  }, [companySlug]);

  const addUser = async () => {
    if (!newUsername.trim() || !newPassword.trim()) return;
    setSaving(true);
    try {
      const email = `${newUsername.trim().toLowerCase()}@webvonk.app`;

      // Step 1: signup
      const signupRes = await fetch(`${WEBVONK_URL}/auth/v1/signup`, {
        method: 'POST',
        headers: { 'apikey': WEBVONK_ANON_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: newPassword }),
      });
      const signupJson = await signupRes.json();
      if (!signupRes.ok) {
        const code = signupJson?.error_code || '';
        if (code === 'user_already_exists' || code === 'email_exists') showToast('Gebruikersnaam al in gebruik', 'error');
        else if (code === 'over_email_send_rate_limit') showToast('Te veel aanmaken, wacht even', 'error');
        else showToast('Aanmaken mislukt', 'error');
        return;
      }

      const newUserId = signupJson?.id || signupJson?.user?.id;
      if (!newUserId) { showToast('Geen gebruikers-ID ontvangen', 'error'); return; }

      // Step 2: create profile via RPC
      const rpcRes = await fetch(`${WEBVONK_URL}/rest/v1/rpc/admin_create_user_profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'apikey': WEBVONK_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          p_user_id: newUserId,
          p_username: newUsername.trim(),
          p_company_slug: companySlug,
          p_role_id: parseInt(newRoleId),
          p_is_active: newActive,
        }),
      });

      if (!rpcRes.ok) { showToast('Profiel aanmaken mislukt', 'error'); return; }

      showToast('Gebruiker toegevoegd');
      setShowAdd(false);
      setNewUsername(''); setNewPassword(''); setNewRoleId('3'); setNewActive(true);
      await loadUsers();
    } catch (e) {
      console.error(e);
      showToast('Fout bij aanmaken', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (u) => {
    try {
      const res = await fetch(`${WEBVONK_URL}/rest/v1/rpc/admin_delete_user_profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'apikey': WEBVONK_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ p_user_id: u.User_id }),
      });
      if (!res.ok) { showToast('Verwijderen mislukt', 'error'); return; }
      showToast('Gebruiker verwijderd');
      setUsers(prev => prev.filter(x => x.User_id !== u.User_id));
    } catch (e) {
      console.error(e);
      showToast('Fout bij verwijderen', 'error');
    }
  };

  const anyModal = showAdd || !!confirmDelete;

  const developerUsers = users.filter(u => String(u.Role_id) === '1');
  const managerUsers = users.filter(u => String(u.Role_id) === '2');
  const staffUsers = users.filter(u => String(u.Role_id) === '3');

  const renderUserRow = (u) => (
    <Row key={u.User_id}>
      <Avatar><User size={16} /></Avatar>
      <UserInfo>
        <Username>{u.Username}</Username>
        <RoleBadge $color={ROLE_COLORS[String(u.Role_id)]}>{ROLES[String(u.Role_id)] || 'Onbekend'}</RoleBadge>
      </UserInfo>
      <StatusDot $active={u.Is_active} title={u.Is_active ? 'Actief' : 'Inactief'} />
      {canDelete(u) && (
        <IconBtn onClick={() => setConfirmDelete(u)}>
          <X size={14} />
        </IconBtn>
      )}
    </Row>
  );

  return (
    <>
      <Container $blurred={anyModal}>

        {toast && <Toast $type={toast.type}>{toast.msg}</Toast>}
        <Content>
          <PageTitle>Gebruikers</PageTitle>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>Laden...</div>
          ) : (
            <>
              {developerUsers.length > 0 && (
                <Section>
                  <SectionLabel>Ontwikkelaars</SectionLabel>
                  <Card>{developerUsers.map(renderUserRow)}</Card>
                </Section>
              )}

              {managerUsers.length > 0 && (
                <Section>
                  <SectionLabel>Eigenaren</SectionLabel>
                  <Card>{managerUsers.map(renderUserRow)}</Card>
                </Section>
              )}

              <Section>
                <SectionLabel>Medewerkers</SectionLabel>
                <Card>
                  {staffUsers.map(renderUserRow)}
                  {(myRoleId === '1' || myRoleId === '2') && (
                    <AddRow onClick={() => { setShowAdd(true); setNewUsername(''); setNewPassword(''); setNewRoleId('3'); setNewActive(true); }}>
                      <UserPlus size={16} />
                      Gebruiker toevoegen
                    </AddRow>
                  )}
                </Card>
              </Section>
            </>
          )}
        </Content>
      </Container>

      {/* Add user modal */}
      {showAdd && (
        <ModalOverlay onClick={() => setShowAdd(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Gebruiker toevoegen</ModalTitle>
              <CloseBtn onClick={() => setShowAdd(false)}><X size={16} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <div>
                <FieldLabel>Gebruikersnaam</FieldLabel>
                <TextInput
                  type="text"
                  value={newUsername}
                  onChange={e => setNewUsername(e.target.value)}
                  placeholder="gebruikersnaam"
                  autoFocus
                />
              </div>
              <div>
                <FieldLabel>Wachtwoord</FieldLabel>
                <PasswordWrap>
                  <TextInput
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{ paddingRight: 36 }}
                  />
                  <PasswordToggle type="button" onClick={() => setShowPassword(p => !p)}>
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </PasswordToggle>
                </PasswordWrap>
              </div>
              <div>
                <FieldLabel>Rol</FieldLabel>
                <SegmentControl>
                  <Segment $active={newRoleId === '3'} onClick={() => setNewRoleId('3')}>Medewerker</Segment>
                  {myRoleId === '1' && <Segment $active={newRoleId === '2'} onClick={() => setNewRoleId('2')}>Eigenaar</Segment>}
                </SegmentControl>
              </div>
              <ToggleRow>
                <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>Actief</span>
                <ToggleTrack $on={newActive} onClick={() => setNewActive(p => !p)} />
              </ToggleRow>
            </ModalBody>
            <ModalActions>
              <SecondaryBtn onClick={() => setShowAdd(false)}>Annuleren</SecondaryBtn>
              <PrimaryBtn onClick={addUser} disabled={!newUsername.trim() || !newPassword.trim() || saving}>
                {saving ? 'Bezig...' : 'Toevoegen'}
              </PrimaryBtn>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <ModalOverlay onClick={() => setConfirmDelete(null)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <ModalHeader>
              <ModalTitle>Gebruiker verwijderen?</ModalTitle>
              <CloseBtn onClick={() => setConfirmDelete(null)}><X size={16} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                Weet je zeker dat je <strong style={{ color: '#fff' }}>{confirmDelete.Username}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
              </div>
            </ModalBody>
            <ModalActions>
              <SecondaryBtn onClick={() => setConfirmDelete(null)}>Annuleren</SecondaryBtn>
              <PrimaryBtn $danger onClick={async () => { await deleteUser(confirmDelete); setConfirmDelete(null); }}>Verwijderen</PrimaryBtn>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}
